/**
 * Вопросы клиентов (#843) и причины отказа (#844) в «Голосе клиента» — строки
 * блоков без React. Спека: docs/plans/2026-10-02-customer-questions-and-decline-reasons.md §4.
 *
 * Строки — те же `VoiceRow`, что у остальных вкладок раздела: одна карточка,
 * одно раскрытие до разговоров, одна выгрузка. У каждого блока своё N
 * (`VoiceSection.callsInWindow`): частота вопросов — разговоры, где шаг
 * отработал; первая минута — такие звонки и (с `includesChats`) чаты; причины — отказы.
 *
 * Блока «вопросы перед решением» нет намеренно: поле `phase` не прошло замер
 * выпуска (О1, docs/eval/2026-10-03-voice-extraction-measurement.md).
 */
import {
  VOICE_BREAKDOWN_OPERATOR_MIN_CALLS,
  type CrmStageNamesReference,
  type CrmStageNamesResponse,
  type VoiceCustomerQuestions,
  type VoiceItem,
  type VoiceSeriesCallBucket,
} from '@aica/api-types/schemas';
import { crmNameOfInstallation, stageNamesOfInstallation } from '@/lib/crm-stage-names';
import { resolveStageBadgeData } from '@/lib/records/resolve-stage-badge';
import type {
  VoiceRow,
  VoiceRowItem,
  VoiceSection,
  VoiceSectionCap,
} from './voice-view-model';
import { declineReasonLabel } from './voice-view-model';

/** Сколько последних формулировок строка вопроса показывает, не раскрываясь (спека §4). */
export const QUESTION_WORDING_PREVIEW = 3;

type Translate = (key: string, options?: Record<string, unknown>) => string;

const toItems = (items: readonly VoiceItem[]): VoiceRowItem[] =>
  items.map((item) => ({ ...item, detail: null }));

/** i18n-ключи текстов «первой минуты», зависящие от того, кого блок считает. */
export interface FirstMinuteTextKeys {
  /** Подсказка блока: правило счёта. */
  hint: string;
  /** Заголовочное N блока. */
  window: string;
  /** «В K из N …» строки. */
  counts: string;
  /** N = 0. */
  zero: string;
  /** N > 0, строк нет. */
  empty: string;
  /** Примечание о знаменателях вопросов. */
  denominatorNote: string;
  /** Что считает N — столбец таблицы выгрузки. */
  base: string;
}

/**
 * Тексты «первой минуты» по ответу. `includesChats` (#1578) — блок считает и
 * чаты: «звонки и чаты». Нет поля — ответ edge-api до #1578 (или из его
 * кеша), где блок считал только звонки: прежние тексты «только звонки».
 */
export function firstMinuteTextKeys(cq: VoiceCustomerQuestions): FirstMinuteTextKeys {
  return cq.firstMinute.includesChats === true
    ? {
        hint: 'voicePage.questions.firstMinuteCallsAndChats',
        window: 'voicePage.questions.windowFirstMinuteWithChats',
        counts: 'voicePage.questions.firstMinuteWithChats',
        zero: 'voicePage.questions.noFirstMinuteMeasured',
        empty: 'voicePage.empty.firstMinuteWithChats',
        denominatorNote: 'voicePage.questions.denominatorNoteWithChats',
        base: 'voicePage.export.base.firstMinuteWithChats',
      }
    : {
        hint: 'voicePage.questions.firstMinuteCallsOnly',
        window: 'voicePage.questions.windowFirstMinute',
        counts: 'voicePage.questions.firstMinuteCalls',
        zero: 'voicePage.questions.noCallsCollected',
        empty: 'voicePage.empty.firstMinute',
        denominatorNote: 'voicePage.questions.denominatorNote',
        base: 'voicePage.export.base.firstMinute',
      };
}

/** Чаще — выше; при равенстве — порядок набора кодов (сортировка устойчива). */
const byCallsDesc = (a: VoiceRow, b: VoiceRow) => b.calls - a.calls;

/**
 * Что говорит блок с N = 0. «Не собрано» — только когда шаг не отработал ни
 * в одном разговоре окна; иначе ноль — измерение: отказов в окне не было или
 * разговоров, где первую минуту можно измерить, нет (окно из одних чатов, а
 * блок считает только звонки, или из чатов, собранных до #1578).
 */
function zeroKey(
  cq: VoiceCustomerQuestions,
  block: 'questions' | 'firstMinute' | 'declines'
): string {
  if (cq.coverage.collectedCalls === 0 || block === 'questions')
    return 'voicePage.questions.notCollected';
  if (block === 'firstMinute') return firstMinuteTextKeys(cq).zero;
  // No sales conversation collected: the outcome was never asked, so «no
  // declines» would be a non-measurement passed off as a zero.
  return cq.declines.salesCallsInWindow === 0
    ? 'voicePage.declines.noSales'
    : 'voicePage.empty.declines';
}

/** «Частые вопросы по предмету»: все предметы, с нулями (ноль показывается, #699). */
export function questionSection(cq: VoiceCustomerQuestions): VoiceSection {
  const { questions } = cq;
  return {
    id: 'questions',
    callsInWindow: questions.callsInWindow,
    baseKey: 'voicePage.export.base.questions',
    zeroKey: zeroKey(cq, 'questions'),
    rows: questions.rows
      .map((row): VoiceRow => ({
        id: `question:${row.code}`,
        kind: 'question',
        key: row.code,
        calls: row.calls,
        prevCalls: row.prevCalls,
        ...(row.mark ? { mark: row.mark } : {}),
        callsInWindow: questions.callsInWindow,
        prevCallsInWindow: questions.prevCallsInWindow,
        items: toItems(row.items),
      }))
      .sort(byCallsDesc),
  };
}

/**
 * «Вопросы без ответа»: предметы с наибольшей долей разговоров, где вопрос
 * остался без полного ответа (`answered ≠ full`), — среди разговоров с ЭТИМ
 * вопросом, а не всего окна: иначе редкий, но всегда неотвеченный вопрос
 * потерялся бы под частым. Прошлого окна у этой доли в ответе нет — сдвига
 * строка не показывает. Только предметы, где такой разговор есть.
 */
export function unansweredSection(cq: VoiceCustomerQuestions): VoiceSection {
  const share = (row: VoiceRow) =>
    row.callsInWindow > 0 ? row.calls / row.callsInWindow : 0;
  return {
    id: 'unanswered',
    callsInWindow: cq.questions.callsInWindow,
    baseKey: 'voicePage.export.base.unanswered',
    // The block's N is the conversations with questions collected.
    blockBaseKey: 'voicePage.export.base.questions',
    zeroKey: zeroKey(cq, 'questions'),
    // No question at all is not «every question got a full answer».
    ...(cq.questions.rows.every((row) => row.calls === 0)
      ? { emptyKey: 'voicePage.empty.questions' }
      : {}),
    rows: cq.questions.rows
      .filter((row) => row.unansweredCalls > 0)
      .map((row): VoiceRow => ({
        id: `unanswered:${row.code}`,
        kind: 'unanswered',
        key: row.code,
        calls: row.unansweredCalls,
        prevCalls: 0,
        callsInWindow: row.calls,
        prevCallsInWindow: 0,
        // The response has no previous window for «without an answer».
        noPrevious: true,
        items: toItems(row.unansweredItems),
      }))
      .sort((a, b) => share(b) - share(a) || b.calls - a.calls),
  };
}

/** «Вопросы первой минуты» — звонки и, если ответ так говорит, чаты (#1578). */
export function firstMinuteSection(cq: VoiceCustomerQuestions): VoiceSection {
  const { firstMinute } = cq;
  const keys = firstMinuteTextKeys(cq);
  return {
    id: 'firstMinute',
    callsInWindow: firstMinute.callsInWindow,
    baseKey: keys.base,
    zeroKey: zeroKey(cq, 'firstMinute'),
    emptyKey: keys.empty,
    rows: firstMinute.rows
      .map((row): VoiceRow => ({
        id: `firstMinute:${row.code}`,
        kind: 'firstMinute',
        key: row.code,
        calls: row.calls,
        prevCalls: row.prevCalls,
        ...(row.mark ? { mark: row.mark } : {}),
        callsInWindow: firstMinute.callsInWindow,
        prevCallsInWindow: firstMinute.prevCallsInWindow,
        countsKey: keys.counts,
        items: toItems(row.items),
      }))
      .sort(byCallsDesc),
  };
}

/**
 * «Почему отказываются»: «причина — в K из N отказов». Доли названных клиентом
 * (`statedCalls`) экран не показывает: поле `stated` не прошло замер выпуска
 * (0,78 при пороге 0,8, О1 — docs/eval/2026-10-03-voice-extraction-measurement.md);
 * ответ сервера её несёт, строка появится, когда мера будет пройдена.
 */
export function declineSection(cq: VoiceCustomerQuestions): VoiceSection {
  const { declines } = cq;
  return {
    id: 'declines',
    callsInWindow: declines.callsInWindow,
    baseKey: 'voicePage.export.base.declines',
    zeroKey: zeroKey(cq, 'declines'),
    rows: declines.rows
      .map((row): VoiceRow => ({
        id: `decline:${row.code}`,
        kind: 'decline',
        key: row.code,
        calls: row.calls,
        prevCalls: row.prevCalls,
        ...(row.mark ? { mark: row.mark } : {}),
        callsInWindow: declines.callsInWindow,
        prevCallsInWindow: declines.prevCallsInWindow,
        items: toItems(row.items),
      }))
      .sort(byCallsDesc),
  };
}

/**
 * The questions answer's cap, carried by each of its blocks: the export holds
 * blocks of other requests too (objections on Sales, themes on both, #2009),
 * and a caveat at the top of the report would speak for them.
 */
function questionsCap(cq: VoiceCustomerQuestions): VoiceSectionCap {
  return {
    truncated: cq.truncated,
    prevTruncated: cq.prevTruncated,
    truncatedKey: 'voicePage.truncated',
    prevTruncatedKey: 'voicePage.prevTruncated',
  };
}

/** Блоки вкладки «Сайт и контент» — они же её выгрузка, каждый со своим потолком. */
export function siteQuestionSections(cq: VoiceCustomerQuestions): VoiceSection[] {
  const cap = questionsCap(cq);
  return [questionSection(cq), firstMinuteSection(cq)].map((section) => ({
    ...section,
    cap,
  }));
}

/**
 * Примечание о знаменателях блоков «Вопросов» на «Продажах» — на экране и в
 * печатной форме одно: первой минуты на вкладке нет, а у «без ответа» свой N.
 */
export const salesQuestionsDenominatorNote =
  'voicePage.questions.denominatorNoteQuestions';

/**
 * Выгрузка вкладки «Продажи» (#2315) — блоки разделов «Отказы» и «Вопросы» в
 * порядке экрана. Разбивки отказов (номер звонка, сотрудник, этап) в неё не
 * входят: в них имена сотрудников, а выгрузка уходит тем, кто в Aelo не
 * заходит. Рядом с ними — блок возражений своего запроса (#2350), поэтому
 * предел выборки каждый блок называет сам (`cap`), а не строка всего отчёта.
 */
export function salesQuestionSections(cq: VoiceCustomerQuestions): VoiceSection[] {
  const cap = questionsCap(cq);
  return [declineSection(cq), unansweredSection(cq), questionSection(cq)].map(
    (section) => ({ ...section, cap })
  );
}

/**
 * Строка разбивки отказов (номер звонка серии, сотрудник, этап): сколько
 * отказов и какие причины. `calls` включает отказы без причины — их остаток
 * назван отдельно, чтобы сумма причин не выглядела всеми отказами.
 *
 * Каждая клетка строки — причина и «причина не названа» — раскрывается до
 * своих разговоров (`items`, #1641), как строка «Почему отказываются». Нет
 * разговоров в ответе (ответ edge-api до #1641) — клетка остаётся числом.
 * Итог строки — сумма её клеток, поэтому сам не раскрывается.
 */
export interface DeclineBreakdownRow {
  id: string;
  label: string;
  calls: number;
  reasons: { code: string; label: string; calls: number; items?: VoiceRowItem[] }[];
  /** Отказы строки, у которых причина не названа. */
  withoutReason: number;
  /** Их разговоры. */
  withoutReasonItems?: VoiceRowItem[];
}

interface BreakdownColumn {
  calls: number;
  reasons: readonly { code: string; calls: number; items?: readonly VoiceItem[] }[];
  withoutReasonItems?: readonly VoiceItem[];
}

function breakdownRow(
  id: string,
  label: string,
  column: BreakdownColumn,
  t: Translate
): DeclineBreakdownRow {
  const named = column.reasons.reduce((sum, reason) => sum + reason.calls, 0);
  return {
    id,
    label,
    calls: column.calls,
    reasons: [...column.reasons]
      .sort((a, b) => b.calls - a.calls)
      .map((reason) => ({
        code: reason.code,
        label: declineReasonLabel(reason.code, t),
        calls: reason.calls,
        ...(reason.items ? { items: toItems(reason.items) } : {}),
      })),
    withoutReason: Math.max(0, column.calls - named),
    ...(column.withoutReasonItems
      ? { withoutReasonItems: toItems(column.withoutReasonItems) }
      : {}),
  };
}

/** «Причина × номер звонка серии» — все корзины, в порядке контракта. */
export function callNumberBreakdown(
  cq: VoiceCustomerQuestions,
  t: Translate
): DeclineBreakdownRow[] {
  return cq.declines.byCallNumber.map((bucket) =>
    breakdownRow(
      `call:${bucket.bucket}`,
      t(`voicePage.declines.byCallNumber.bucket.${bucketKey(bucket.bucket)}`),
      bucket,
      t
    )
  );
}

/** `4+` не годится в ключ перевода. */
function bucketKey(bucket: VoiceSeriesCallBucket): string {
  return bucket === '4+' ? 'fourPlus' : bucket;
}

/** «Причина × сотрудник» — больше отказов выше, как отдал сервер. */
export function operatorBreakdown(
  cq: VoiceCustomerQuestions,
  t: Translate
): DeclineBreakdownRow[] {
  return cq.declines.byOperator.map((operator) =>
    breakdownRow(
      // Один id CRM в двух порталах — два человека: строку различает установка.
      `operator:${JSON.stringify([operator.installationId ?? null, operator.agentId])}`,
      operator.agentName ?? t('voicePage.declines.byOperator.unnamed'),
      operator,
      t
    )
  );
}

/**
 * Отказы без ответственного сотрудника — из тех же разговоров под потолком,
 * что строки сотрудников (`declines.unassignedCalls`). Ответ edge-api без
 * этого поля: остаток «все отказы − строки» верен, только пока счёт не упёрся
 * в потолок, — иначе `null`, и экран ничего не утверждает.
 */
export function unassignedDeclines(cq: VoiceCustomerQuestions): number | null {
  if (cq.declines.unassignedCalls !== undefined) return cq.declines.unassignedCalls;
  // An edge-api before both fields: no employee was held back by the call
  // threshold, so «all − rows» is still the unassigned remainder.
  if (cq.truncated) return null;
  const assigned = cq.declines.byOperator.reduce((sum, row) => sum + row.calls, 0);
  return Math.max(0, cq.declines.callsInWindow - assigned);
}

/** Строка под разбивкой по сотрудникам: отказы, которых нет в её строках. */
export interface DeclineBreakdownNote {
  key: string;
  text: string;
  calls: number;
  items?: VoiceRowItem[];
}

/**
 * Строки под разбивкой по сотрудникам — отказы, которые строки не показывают,
 * каждая раскрывается до своих разговоров (#1641): сотрудники с меньше чем
 * VOICE_BREAKDOWN_OPERATOR_MIN_CALLS разговорами продаж (решение владельца
 * 05.10, #1687) и отказы без сотрудника. Строки + эти числа = все отказы.
 */
export function operatorBreakdownNotes(
  cq: VoiceCustomerQuestions,
  t: Translate
): DeclineBreakdownNote[] {
  const notes: DeclineBreakdownNote[] = [];
  const below = cq.declines.belowThresholdCalls ?? 0;
  if (below > 0) {
    const items = cq.declines.belowThresholdItems;
    notes.push({
      key: 'belowThreshold',
      text: t('voicePage.declines.byOperator.belowThreshold', {
        count: below,
        min: VOICE_BREAKDOWN_OPERATOR_MIN_CALLS,
      }),
      calls: below,
      ...(items ? { items: toItems(items) } : {}),
    });
  }
  const unassigned = unassignedDeclines(cq);
  if (unassigned !== null && unassigned > 0) {
    const items = cq.declines.unassignedItems;
    notes.push({
      key: 'unassigned',
      text: t('voicePage.declines.byOperator.unassigned', { count: unassigned }),
      calls: unassigned,
      ...(items ? { items: toItems(items) } : {}),
    });
  }
  return notes;
}

/**
 * Что значит разбивка по сотрудникам без строк: ни у одного сотрудника нет
 * порога разговоров — или у отказов вовсе нет ответственного сотрудника.
 */
export function operatorBreakdownEmptyText(
  cq: VoiceCustomerQuestions,
  t: Translate
): string {
  return (cq.declines.belowThresholdCalls ?? 0) > 0
    ? t('voicePage.declines.byOperator.noneAboveThreshold', {
        min: VOICE_BREAKDOWN_OPERATOR_MIN_CALLS,
      })
    : t('voicePage.declines.byOperator.noneAssigned');
}

/**
 * Состояние блоков в ответе — одно правило для «Продаж» и «Сайта и
 * контента»: блоки пришли; шлюз открыт, но чтение упало (`failed`, экран
 * предлагает повтор); ключа нет и сбоя нет — шлюз закрыт.
 */
export type CustomerQuestionsState =
  { kind: 'ready'; cq: VoiceCustomerQuestions } | { kind: 'failed' } | { kind: 'closed' };

export function customerQuestionsState(data: {
  customerQuestions?: VoiceCustomerQuestions;
  customerQuestionsUnavailable?: boolean;
}): CustomerQuestionsState {
  if (data.customerQuestions) return { kind: 'ready', cq: data.customerQuestions };
  return data.customerQuestionsUnavailable === true
    ? { kind: 'failed' }
    : { kind: 'closed' };
}

type StageRow = NonNullable<VoiceCustomerQuestions['declines']['byStage']>[number];

/** Длинный id этапа CRM укорачивается: подпись строки, не ключ. */
const STAGE_ID_MAX = 24;
const shortId = (id: string) =>
  id.length > STAGE_ID_MAX ? `${id.slice(0, STAGE_ID_MAX - 1)}…` : id;

/**
 * Подпись этапа. Имя — из справочника CRM установки строки
 * (`GET /crm/stage-names`, #2872; id портально-локальны, и список другой CRM
 * назвал бы этап чужим именем) тем же правилом, что у бейджа этапа в карточке
 * разговора (`resolveStageBadgeData`): по паре конвейер + этап, второго
 * правила поиска нет. Справочник — этапы СДЕЛОК,
 * поэтому имя получает только этап сделки: лид с тем же id («NEW») — другой
 * этап.
 *
 * Без имени — фаза этапа, а за ней — короткий id этапа (и конвейер, и тип
 * сущности, когда в разбивке их несколько): две строки одной фазы иначе
 * читались бы одной.
 */
export function stageLabel(
  stage: Pick<
    StageRow,
    'stageKey' | 'stageId' | 'pipelineId' | 'entityType' | 'semantic'
  >,
  reference: CrmStageNamesReference['pipelines'] | undefined,
  t: Translate,
  context: { multiplePipelines: boolean; multipleEntities: boolean } = {
    multiplePipelines: false,
    multipleEntities: false,
  }
): string {
  if (stage.stageKey === null) return t('voicePage.declines.byStage.noStage');
  if (stage.entityType === 'deal' && reference && reference.length > 0) {
    const named = resolveStageBadgeData({
      crmPipelineId: stage.pipelineId,
      crmStageId: stage.stageId,
      promptProfiles: [],
      crmStagesReference: reference,
    });
    if (named.crmStageName) {
      return named.crmMultiplePipelines && named.crmPipelineName
        ? t('voicePage.declines.byStage.withPipeline', {
            stage: named.crmStageName,
            pipeline: named.crmPipelineName,
          })
        : named.crmStageName;
    }
  }
  const phase = stage.semantic
    ? t('voicePage.declines.byStage.phase', {
        phase: t(`records:records.stageSemantic.${stage.semantic}`, {
          defaultValue: stage.semantic,
        }),
      })
    : t('voicePage.declines.byStage.unknown');
  const detail = [
    context.multipleEntities && stage.entityType
      ? t(`common:routing.fields.${stage.entityType}`, { defaultValue: stage.entityType })
      : '',
    context.multiplePipelines && stage.pipelineId
      ? t('voicePage.declines.byStage.pipelineRef', { id: shortId(stage.pipelineId) })
      : '',
    stage.stageId ? shortId(stage.stageId) : '',
  ].filter(Boolean);
  return detail.length > 0
    ? t('voicePage.declines.byStage.withDetail', {
        label: phase,
        detail: detail.join(', '),
      })
    : phase;
}

/** «Причина × этап воронки» — только когда сервер прислал разбивку (stageAwareScoring). */
export function stageBreakdown(
  cq: VoiceCustomerQuestions,
  names: CrmStageNamesResponse | undefined,
  t: Translate
): DeclineBreakdownRow[] | null {
  const byStage = cq.declines.byStage;
  if (!byStage) return null;
  const staged = byStage.filter((stage) => stage.stageKey !== null);
  const context = {
    multiplePipelines: new Set(staged.map((stage) => stage.pipelineId ?? '')).size > 1,
    multipleEntities: new Set(staged.map((stage) => stage.entityType ?? '')).size > 1,
  };
  // Rows of several CRM installs may read alike (the same stage name, the same
  // pipeline id): each says whose it is, as the «Продажи» card does.
  const multipleCrms =
    new Set(staged.map((stage) => stage.installationId ?? '')).size > 1;
  return byStage.map((stage) => {
    const label = stageLabel(
      stage,
      stageNamesOfInstallation(names, stage.installationId),
      t,
      context
    );
    const crm =
      multipleCrms && stage.stageKey !== null
        ? crmNameOfInstallation(names, stage.installationId)
        : undefined;
    return breakdownRow(
      `stage:${stage.installationId ?? ''}:${stage.entityType ?? ''}:${stage.stageKey ?? 'none'}`,
      crm ? `${crm} · ${label}` : label,
      stage,
      t
    );
  });
}

/**
 * Строки об охвате и истории — над блоками, на экране и в печатной форме:
 * знаменатель называется до числителей, а несобранное — отдельной строкой.
 */
export function coverageLines(
  cq: VoiceCustomerQuestions,
  t: Translate,
  formatDate: (iso: string) => string
): string[] {
  const { coverage } = cq;
  return [
    t('voicePage.questions.coverage', {
      collected: coverage.collectedCalls,
      count: coverage.analyzedCalls,
      notCollected: coverage.notCollectedCalls,
    }),
    coverage.dataSince
      ? t('voicePage.questions.dataSince', { date: formatDate(coverage.dataSince) })
      : t('voicePage.questions.noDataYet'),
  ];
}

/**
 * Строки печатной формы вкладки с блоками вопросов («Продажи», «Сайт и
 * контент») — в том же порядке, что над и под блоками на экране: охват,
 * подсказка вкладки, оговорки о полноте счёта, примечания её блоков.
 */
export function questionsPrintNotes(
  cq: VoiceCustomerQuestions,
  t: Translate,
  formatDate: (iso: string) => string,
  options: {
    /** A theme filter without a comparable previous window (#2229): no previous lines. */
    comparable: boolean;
    hint?: string;
    /** The notes under the tab's blocks: the denominators, the wordings. */
    notes: readonly string[];
    /** The blocks print their own cap (`VoiceSection.cap`, Sales — #2350). */
    capsInBlocks?: boolean;
  }
): string[] {
  const caps = !options.capsInBlocks;
  return [
    ...coverageLines(cq, t, formatDate),
    options.hint ?? '',
    caps && cq.truncated ? t('voicePage.truncated') : '',
    caps && options.comparable && cq.prevTruncated ? t('voicePage.prevTruncated') : '',
    ...options.notes,
  ].filter(Boolean);
}
